import React, { useEffect, useState } from 'react';
import CashRegisterIcon from '../icons/CashRegisterIcon';
import RegisterModal from './RegisterModal';
import type { Order } from '../../types';
import { isManager } from '../../lib/authUtils';

interface RegisterClosedGuardProps {
  onOpenRegister: (openingFloat: number) => void;
  todaysOrders?: Order[];
  currentUser?: any; // StaffMember or CurrentUser
  /**
   * True until the global `subscribeActive` listener in App.tsx has delivered
   * its first snapshot. While true we render a neutral "Checking register
   * status…" view instead of the "Open Cash Register" button, preventing the
   * race that historically let a cashier open a duplicate session before the
   * shared session loaded.
   */
  isLoading?: boolean;
  /**
   * Hide the "Open Cash Register" button entirely (e.g. on the Kitchen
   * Display, where kitchen staff should never open a register — that's a
   * cashier action). The closed-state view becomes a passive "waiting on
   * the cashier" message.
   */
  hideOpenButton?: boolean;
}

// If the parent never flips `isLoading` to false within this window we
// assume something went wrong with the live subscription (permission error,
// totally offline, etc.) and switch to a "Retry" view rather than spinning
// forever. Tuned to be longer than a cold Firestore subscribe (~1-3s in
// practice) but short enough that staff don't sit on a spinner.
const HYDRATION_TIMEOUT_MS = 5000;

const RegisterClosedGuard: React.FC<RegisterClosedGuardProps> = ({ onOpenRegister, todaysOrders = [], currentUser, isLoading = false, hideOpenButton = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const userIsManager = isManager(currentUser);

  // Spinner-stuck fallback: if isLoading stays true past HYDRATION_TIMEOUT_MS,
  // surface a Retry view. Retry just clears the local flag - the parent's
  // subscription is still trying in the background, so a re-render eventually
  // gets us through.
  const [hydrationTimedOut, setHydrationTimedOut] = useState(false);
  useEffect(() => {
    if (!isLoading) {
      setHydrationTimedOut(false);
      return;
    }
    setHydrationTimedOut(false);
    const t = setTimeout(() => setHydrationTimedOut(true), HYDRATION_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [isLoading]);

  const handleOpenClick = () => {
    if (!userIsManager) {
      alert('Only managers can open the register. Please contact a manager.');
      return;
    }
    setIsModalOpen(true);
  };

  const handleOpenRegister = (openingFloat: number) => {
    onOpenRegister(openingFloat);
    setIsModalOpen(false);
  };

  // ─── Loading state (Layer 1: UI hydration gate) ──────────────────────────
  // Until we know whether a shared session exists, render a neutral panel.
  // Same outer layout as the closed-register view so the page doesn't jump
  // when we transition from loading -> closed -> (maybe) open.
  if (isLoading && !hydrationTimedOut) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-6">
        <div className="text-center max-w-md">
          <div className="mb-6 flex justify-center">
            <div className="w-24 h-24 rounded-full border-4 border-gray-200 border-t-blue-500 animate-spin" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-3">Checking register status…</h1>
          <p className="text-gray-600">
            Looking for an open register session across all devices. This usually takes a second.
          </p>
        </div>
      </div>
    );
  }

  // ─── Hydration timed out ─────────────────────────────────────────────────
  // The listener still hasn't reported. Don't show the Open button (we have no
  // way of knowing if a session is already open) - offer a Retry instead.
  if (isLoading && hydrationTimedOut) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 p-6">
        <div className="text-center max-w-md">
          <div className="mb-6 flex justify-center">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center">
              <span className="text-amber-600 font-bold text-3xl">?</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-3">Unable to verify register status</h1>
          <p className="text-gray-600 mb-6">
            We couldn't reach the live register feed. Check your internet connection, then retry.
            Opening the register before verification could create a duplicate session.
          </p>
          <button
            onClick={() => setHydrationTimedOut(false)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ─── Closed state (original UI) ──────────────────────────────────────────
  // When `hideOpenButton` is set (KDS), the Open Cash Register button must
  // never appear, regardless of role. KDS staff shouldn't open the register —
  // that's a cashier action on POS. Treat the screen as read-only and show a
  // waiting message.
  const allowOpenButton = userIsManager && !hideOpenButton;
  return (
    <>
      <div
        className="h-full flex flex-col items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 p-6"
        data-testid="register-closed-guard"
      >
        <div className="text-center">
          <div className="mb-6 flex justify-center">
            <div className="relative">
              <CashRegisterIcon className="w-24 h-24 text-red-500" />
              <div className="absolute top-0 right-0 w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl">!</span>
              </div>
            </div>
          </div>

          <h1 className="text-4xl font-bold text-gray-800 mb-3">Register Closed</h1>

          {allowOpenButton ? (
            <>
              <p className="text-xl text-gray-700 mb-2">
                The cash register must be opened before you can access this feature.
              </p>

              <div className="bg-white rounded-lg shadow-lg p-6 my-8 max-w-md mx-auto border-2 border-red-200">
                <h3 className="font-bold text-gray-800 mb-3">Why is this required?</h3>
                <ul className="text-left text-gray-600 space-y-2">
                  <li className="flex items-start">
                    <span className="text-red-500 font-bold mr-2">•</span>
                    <span>Track opening float accurately</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 font-bold mr-2">•</span>
                    <span>Reconcile cash at end of day</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 font-bold mr-2">•</span>
                    <span>Prevent unauthorized transactions</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 font-bold mr-2">•</span>
                    <span>Ensure proper audit trail</span>
                  </li>
                </ul>
              </div>

              <button
                onClick={handleOpenClick}
                data-testid="open-cash-register"
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-4 px-8 rounded-lg shadow-lg transition-all duration-200 transform hover:scale-105 text-lg"
              >
                Open Cash Register
              </button>
            </>
          ) : hideOpenButton ? (
            <>
              <p className="text-xl text-gray-700 mb-2">
                Waiting for a manager to open the register on the main POS.
              </p>
              <p className="text-sm text-gray-500 mt-4">
                Phone order-taking unlocks once the shared register is open.
              </p>
            </>
          ) : (
            <>
              <p className="text-xl text-gray-700 mb-2">
                Register must be opened by a manager first.
              </p>
              <p className="text-sm text-gray-500 mt-4">
                Please contact a manager to open the register.
              </p>
            </>
          )}

          {allowOpenButton && (
            <p className="text-gray-500 text-sm mt-6">
              Contact your manager if you need assistance opening the register.
            </p>
          )}
        </div>
      </div>

      {/* Register Modal */}
      {isModalOpen && allowOpenButton && (
        <RegisterModal
          session={undefined}
          sessionSales={{ cash: 0, card: 0 }}
          closedByDisplayName={
            (currentUser?.name && String(currentUser.name).trim()) ||
            (currentUser?.email && String(currentUser.email).trim()) ||
            undefined
          }
          onClose={() => setIsModalOpen(false)}
          onOpenRegister={handleOpenRegister}
          onCloseRegister={() => {}}
        />
      )}
    </>
  );
};

export default RegisterClosedGuard;
